import { createContext, useState, useEffect, useMemo, useContext, type ReactNode } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { identifyUser, resetUser, trackEvent } from "@/lib/posthog";

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signUp: (email: string, password: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, newSession) => {
      setSession(newSession);
      setUser(newSession?.user ?? null);
      
      // Track auth events
      if (event === 'SIGNED_IN' && newSession?.user) {
        identifyUser(newSession.user.id, {
          email: newSession.user.email,
        });
        trackEvent('user_login', {
          method: 'password',
        });
      } else if (event === 'SIGNED_OUT') {
        resetUser();
      }
    });

    supabase.auth.getSession()
      .then(({ data }) => {
        setSession(data.session);
        setUser(data.session?.user ?? null);
        
        // Identify existing user on load
        if (data.session?.user) {
          identifyUser(data.session.user.id, {
            email: data.session.user.email,
          });
        }
      })
      .finally(() => setLoading(false));

    return () => subscription.unsubscribe();
  }, []);

  const signIn: AuthContextValue["signIn"] = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });

    if (error) {
      return { error: error.message };
    }

    // Reset failed attempts on successful login if profile exists
    if (data?.user?.id) {
      try {
        await supabase
          .from('profiles')
          .update({ failed_login_attempts: 0, locked_until: null })
          .eq('id', data.user.id);
      } catch (resetError) {
        // Ignore profile update errors - profile may not exist yet
        console.log('Profile update skipped:', resetError);
      }
    }

    return { error: null };
  };

  const signUp: AuthContextValue["signUp"] = async (email, password) => {
    const redirectUrl = `${window.location.origin}/`;
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: { emailRedirectTo: redirectUrl },
    });
    return { error: error?.message ?? null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const value = useMemo(() => ({ user, session, loading, signIn, signUp, signOut }), [user, session, loading]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
